import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { QR, type QRHandle } from "../lib/qr";
import { BANK_APPS, openBankApp } from "../lib/appLinks";
import { money } from "../lib/format";
import { isUuid } from "../../shared/routeParams";
import { useDialog } from "../lib/useDialog";
import { CheckIcon, ClockIcon, CloseIcon, DashIcon, DownloadIcon, Logo, PayerSteps } from "../lib/ui";
import type { RequestStatus } from "../lib/types";

interface PayData {
  orgName: string;
  reference: string;
  amount: number | null;
  description: string | null;
  status: RequestStatus;
  confirmedAmount: number | null;
  payload: string;
}

type Check =
  | { kind: "idle" }
  | { kind: "checking" }
  | { kind: "pending" }
  | { kind: "error"; message: string };

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div>
      <header className="col" style={{ display: "flex", alignItems: "center", height: 76 }}>
        <Logo height={24} />
      </header>
      <main className="col" style={{ paddingTop: 8, paddingBottom: 48 }}>
        {children}
      </main>
    </div>
  );
}

function PoweredBy({ extra }: { extra?: string }) {
  return (
    <p style={{ margin: "34px 0 0", fontSize: 13.5, color: "var(--ink3)", textAlign: "center" }}>
      Powered by <Link to="/" style={{ fontWeight: 600, color: "var(--ink)" }}>ConfirmPay</Link>
      {extra}
    </p>
  );
}

export default function Pay() {
  const { id = "" } = useParams();
  // The address is the attacker's to write, and `id` is put into API paths:
  // anything that isn't a request id is treated as a dead link and never sent.
  const validId = isUuid(id);
  // Set when the payer came through an embedded button: on the paid screen
  // they're told they can close the tab and carry on where they were.
  const fromButton = useSearchParams()[0].get("from") === "button";
  const [data, setData] = useState<PayData | null>(null);
  const [loadError, setLoadError] = useState("");
  const [status, setStatus] = useState<RequestStatus>("pending");
  const [confirmedAmount, setConfirmedAmount] = useState<number | null>(null);
  const [check, setCheck] = useState<Check>({ kind: "idle" });
  const [email, setEmail] = useState("");
  const [pickerOpen, setPickerOpen] = useState(false);
  const inFlight = useRef(false);
  const qrRef = useRef<QRHandle>(null);
  const saveQrBtnRef = useRef<HTMLButtonElement>(null);
  const sheetCloseRef = useRef<HTMLButtonElement>(null);

  async function handleSaveQr() {
    await qrRef.current?.save();
    setPickerOpen(true);
  }

  // The bank picker is a bottom sheet: focus moves in on open, Escape closes
  // it, Tab wraps inside it, and focus returns to "Save QR to Gallery".
  const sheetRef = useDialog(pickerOpen, () => setPickerOpen(false), sheetCloseRef, saveQrBtnRef);

  useEffect(() => {
    if (!validId) {
      setLoadError("This payment link is invalid or has been removed. Ask the organisation or business for a fresh link.");
      return;
    }
    api.get<PayData>(`/api/pay/${encodeURIComponent(id)}`).then((res) => {
      if (!res.ok) {
        setLoadError(
          res.status === 404
            ? "This payment link is invalid or has been removed. Ask the organisation or business for a fresh link."
            : res.error,
        );
        return;
      }
      setData(res.data);
      setStatus(res.data.status);
      setConfirmedAmount(res.data.confirmedAmount);
    });
  }, [id, validId]);

  const confirmed = status === "confirmed";
  const expired = status === "expired";

  const recheck = useCallback(
    async (opts: { silent: boolean }) => {
      if (!validId || inFlight.current || confirmed || expired) return;
      inFlight.current = true;
      if (!opts.silent) setCheck({ kind: "checking" });
      const res = await api.post<{
        status: RequestStatus;
        confirmed: boolean;
        amount: number | null;
      }>(`/api/requests/${encodeURIComponent(id)}/status`, { payerEmail: email.trim() || undefined });
      inFlight.current = false;
      if (res.ok && res.data.status === "confirmed") {
        setStatus("confirmed");
        setConfirmedAmount(res.data.amount);
        setCheck({ kind: "idle" });
      } else if (res.ok && res.data.status === "expired") {
        // The link's 3-hour window passed while this tab sat open — the
        // next render swaps to the expired page below.
        setStatus("expired");
        setCheck({ kind: "idle" });
      } else if (!res.ok && !opts.silent) {
        setCheck({ kind: "error", message: res.error });
      } else if (!opts.silent) {
        setCheck({ kind: "pending" });
      } else {
        setCheck({ kind: "idle" });
      }
    },
    [confirmed, expired, email, id, validId],
  );

  // Silent re-check both ways: on return (the bank email may have landed while they were away)
  // and on hide (the moment they switch to their banking app). Either call also persists the
  // email currently in the field. Hiding matters on its own: switching apps doesn't reliably fire
  // the input's blur, so a payer who types an email and then goes to pay would otherwise lose it.
  useEffect(() => {
    function onVisibilityChange() {
      recheck({ silent: true });
    }
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => document.removeEventListener("visibilitychange", onVisibilityChange);
  }, [recheck]);

  if (loadError) {
    return (
      <Shell>
        <h1 className="h1" style={{ fontSize: 40 }}>Payment link</h1>
        <div className="error-box" role="alert" style={{ marginTop: 18 }}>{loadError}</div>
      </Shell>
    );
  }
  if (!data) {
    return (
      <Shell>
        <div className="note note--wait" role="status" style={{ marginTop: 0 }}>
          <span className="spin" /> Loading…
        </div>
      </Shell>
    );
  }

  if (expired) {
    return (
      <Shell>
        <div style={{ paddingTop: 28 }}>
          <span className="ico ico--exp" style={{ width: 64, height: 64 }}>
            <DashIcon size={28} strokeWidth={2.2} />
          </span>
          <h1 className="h1" style={{ fontSize: 40, letterSpacing: "-0.045em", marginTop: 22 }}>
            Payment link expired
          </h1>
          <p style={{ margin: "14px 0 0", fontSize: 18, color: "var(--ink2)", lineHeight: 1.5 }}>
            Please contact {data.orgName} to send a new one.
          </p>
          <p style={{ margin: "18px 0 0", fontSize: 14.5, color: "var(--ink3)" }}>
            Payment links stay open for 3 hours.
          </p>
          <PoweredBy />
        </div>
      </Shell>
    );
  }

  if (confirmed) {
    // Mirrors the receipt email's layout (check, amount, reference/for rows, note), so a payer who
    // gave no email can screenshot this page as their receipt.
    return (
      <Shell>
        <PayerSteps active={3} paid />
        <div style={{ background: "var(--raised)", border: "1px solid var(--line)", borderRadius: 12, padding: "32px 28px", boxShadow: "0 1px 0 var(--line)" }}>
          <span className="ico ico--paid" style={{ width: 72, height: 72 }}>
            <CheckIcon size={36} />
          </span>
          <h1 className="h1" style={{ fontSize: 32, letterSpacing: "-0.04em", lineHeight: 1.05, marginTop: 22 }}>
            Payment confirmed
          </h1>
          <p style={{ margin: "10px 0 0", fontSize: 16, color: "var(--ink2)" }}>
            {data.orgName} has confirmed receipt of your PayNow payment.
          </p>
          <div className="mono" style={{ marginTop: 28, fontSize: 52, fontWeight: 500, letterSpacing: "-0.045em", lineHeight: 1.05 }}>
            {confirmedAmount != null ? money(confirmedAmount) : "—"}
          </div>
          <div className="kv" style={{ marginTop: 18 }}>
            <div className="row"><span>Reference</span><span className="mono">{data.reference}</span></div>
            {data.description && (
              <div className="row"><span>For</span><span>{data.description}</span></div>
            )}
          </div>
          <div className="note note--info" style={{ marginTop: 22, fontSize: 14.5, padding: "16px 18px", borderRadius: 8 }}>
            <span>
              The money went straight from your bank to <strong>{data.orgName}</strong>&rsquo;s own account. ConfirmPay never held or routed it.
            </span>
          </div>
          {fromButton && (
            <div className="note note--ok" style={{ marginTop: 14, fontSize: 14.5, padding: "16px 18px", borderRadius: 8 }}>
              <span>
                <strong>All done here.</strong> You can close this tab and carry on where you left off.
              </span>
            </div>
          )}
        </div>
        <PoweredBy />
      </Shell>
    );
  }

  return (
    <Shell>
      <PayerSteps active={2} />

      <div style={{ fontSize: 16, fontWeight: 600, color: "var(--ink2)" }}>{data.orgName}</div>
      <div className="mono" style={{ fontSize: "min(60px, 15vw)", fontWeight: 500, letterSpacing: "-0.045em", lineHeight: 1.05, marginTop: 4 }}>
        {data.amount != null ? money(data.amount) : "—"}
      </div>
      <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: "8px 12px", marginTop: 10 }}>
        {data.description && <span style={{ fontSize: 17, color: "var(--ink2)" }}>{data.description}</span>}
        <span className="chip chip--wait"><ClockIcon size={12} />Waiting for payment</span>
      </div>

      {/* the PayNow card: unchanged, it is PayNow's own look */}
      <div style={{ marginTop: 26 }}>
        <QR
          ref={qrRef}
          payload={data.payload}
          downloadName={`paynow-${data.reference}.png`}
          tappable={false}
        />
      </div>
      <p style={{ margin: "14px 0", textAlign: "center", fontSize: 14.5, color: "var(--ink3)" }}>
        Amount and reference are pre-filled
      </p>
      <button type="button" className="btn btn--ghost btn--block" style={{ height: 50, fontSize: 16 }} ref={saveQrBtnRef} onClick={handleSaveQr}>
        <DownloadIcon />
        Save QR to Gallery
      </button>

      {pickerOpen && (
        <div className="sheet-backdrop" onClick={() => setPickerOpen(false)}>
          <div
            className="sheet"
            role="dialog"
            aria-modal="true"
            aria-labelledby="bank-picker-title"
            ref={sheetRef}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sheet__handle" />
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16 }}>
              <h2 id="bank-picker-title">Scan from gallery with your banking app</h2>
              <button type="button" className="x" aria-label="Close" ref={sheetCloseRef} onClick={() => setPickerOpen(false)}>
                <CloseIcon />
              </button>
            </div>
            <div className="app-grid">
              {BANK_APPS.map((app) => (
                <button
                  key={app.id}
                  type="button"
                  className="app-btn"
                  onClick={() => {
                    setPickerOpen(false);
                    openBankApp(app);
                  }}
                >
                  <img src={app.logo} alt="" />
                  <span>{app.name}</span>
                </button>
              ))}
            </div>
            <p style={{ margin: "18px 0 0", textAlign: "center", fontSize: 14, color: "var(--ink3)" }}>
              Using a different bank? Just open your app and scan the saved QR.
            </p>
          </div>
        </div>
      )}

      <section style={{ marginTop: 34, borderTop: "1px solid var(--line2)", paddingTop: 26 }}>
        <label className="lbl" htmlFor="pp-email" style={{ fontSize: 15 }}>
          Email for a receipt <span className="opt">(optional)</span>
        </label>
        <input
          id="pp-email"
          className="inp"
          style={{ height: 54, fontSize: 17 }}
          type="email"
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onBlur={() => recheck({ silent: true })}
          placeholder="you@example.com"
        />
        <button
          className="btn btn--block"
          style={{ marginTop: 18, height: 56, fontSize: 17 }}
          disabled={check.kind === "checking"}
          onClick={() => recheck({ silent: false })}
        >
          {check.kind === "checking" ? "Checking…" : "I have made payment"}
        </button>
        <div role="status">
          {check.kind === "pending" && (
            <div className="note note--wait" style={{ marginTop: 16, fontSize: 14.5 }}>
              Not confirmed yet. Give it a minute and try again.
            </div>
          )}
          {check.kind === "error" && (
            <div className="note note--err" style={{ marginTop: 16, fontSize: 14.5 }}>{check.message}</div>
          )}
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginTop: 16, fontSize: 14, color: "var(--ink3)" }}>
          <span>Reference</span>
          <span className="mono" style={{ fontSize: 14, fontWeight: 500, color: "var(--ink)" }}>{data.reference}</span>
        </div>
      </section>

      <PoweredBy extra={`. The money goes straight to ${data.orgName}.`} />
    </Shell>
  );
}
